import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowSquareOut,
  Buildings,
  CheckCircle,
  CircleNotch,
  ClipboardText,
  CurrencyInr,
  MapPin,
  Package,
  PaperPlaneTilt,
  ShieldCheck,
  Storefront,
  TrendUp,
  Truck
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const materialCategories = [
  "Cement",
  "M-Sand & River Sand",
  "Aggregates & Jelly",
  "Bricks & Blocks",
  "TMT Steel",
  "Tiles & Flooring",
  "Paint & Finishes",
  "Plumbing Materials",
  "Electrical Materials",
  "Hardware & Tools",
  "Multiple Categories"
];

const sellableMaterials = [
  "Cement, concrete products, and ready-mix supply",
  "Sand, M-sand, aggregates, jelly, and blue metal",
  "Bricks, blocks, pavers, tiles, and flooring material",
  "TMT steel, binding wire, sheets, pipes, and structural items",
  "Paint, waterproofing, plumbing, electrical, hardware, and tools"
];

const benefits = [
  {
    icon: TrendUp,
    title: "More construction demand",
    description: "Reach contractors, builders, and site buyers who are already ordering materials through Loado."
  },
  {
    icon: ClipboardText,
    title: "Simple order flow",
    description: "Manage product listing, enquiries, orders, and fulfilment from the seller website."
  },
  {
    icon: Truck,
    title: "Logistics support",
    description: "Coordinate delivery movement with Loado's construction logistics network where available."
  },
  {
    icon: ShieldCheck,
    title: "Verified supplier presence",
    description: "Build trust with buyers through clear store details, material categories, and service areas."
  }
];

export default function SellerPortal({ onSubmit }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile_number: "",
    store_name: "",
    store_location_city: "Chennai",
    material_category: "",
    message: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await onSubmit(formData);
    setIsLoading(false);

    if (result.success) {
      setIsSuccess(true);
      setFormData({
        name: "",
        mobile_number: "",
        store_name: "",
        store_location_city: "Chennai",
        material_category: "",
        message: ""
      });
    }
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-[#F8FAFC]">
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <button
            onClick={() => navigate("/")}
            data-testid="seller-back-to-home"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft size={20} weight="bold" />
            <span className="font-medium">Back to Home</span>
          </button>

          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-5">
                <Storefront size={22} weight="duotone" className="text-[#FF5500]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF5500]">
                  Seller Portal
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-[#0F172A] mb-5 leading-tight">
                Sell construction materials through Loado
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
                Loado sellers are construction material stores, stockists, distributors,
                and local suppliers who want to serve verified construction demand with
                faster ordering, listing, and order management.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <a
                  href="https://sell.loado.in"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="seller-portal-external-link"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF5500] text-white font-semibold rounded-lg hover:bg-[#E64D00] transition-colors"
                >
                  Open sell.loado.in
                  <ArrowSquareOut size={20} weight="bold" />
                </a>
                <a
                  href="#supplier-support"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
                >
                  Apply for Chennai
                </a>
              </div>
            </div>

            <div className="bg-[#0F172A] text-white p-6 sm:p-8 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: MapPin, label: "Available city", value: "Chennai" },
                  { icon: Package, label: "Materials", value: "Civil supply" },
                  { icon: Buildings, label: "For", value: "Stores & suppliers" },
                  { icon: CurrencyInr, label: "Platform", value: "sell.loado.in" }
                ].map((item) => (
                  <div key={item.label} className="border border-white/10 p-4 rounded-lg bg-white/5">
                    <item.icon size={24} weight="duotone" className="text-[#FF5500] mb-3" />
                    <p className="text-xs text-slate-400">{item.label}</p>
                    <p className="font-bold mt-1">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-sm text-slate-300 leading-relaxed">
                  Seller onboarding, product listing, order management, and store
                  operations are handled through the dedicated seller website.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <h2 className="text-2xl lg:text-3xl font-black text-[#0F172A] mb-4">
                What can sellers list?
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Loado is built for construction supply categories used by sites,
                contractors, builders, and infrastructure projects.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
              {sellableMaterials.map((material) => (
                <div key={material} className="flex gap-3 p-4 bg-white border border-slate-200 rounded-lg">
                  <CheckCircle size={20} weight="fill" className="text-[#FF5500] mt-0.5 shrink-0" />
                  <p className="text-sm font-medium text-slate-700">{material}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="text-2xl lg:text-3xl font-black text-[#0F172A] mb-4">
              Benefits for Loado sellers
            </h2>
            <p className="text-slate-600 leading-relaxed">
              A seller account helps suppliers move from phone-only sales to a
              cleaner digital workflow for construction material orders.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="p-5 bg-[#F8FAFC] border border-slate-200 rounded-lg">
                <div className="w-11 h-11 bg-[#FF5500]/10 flex items-center justify-center rounded-lg mb-4">
                  <benefit.icon size={24} weight="duotone" className="text-[#FF5500]" />
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="supplier-support" className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <MapPin size={20} weight="duotone" className="text-[#0055FF]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0055FF]">
                  Chennai suppliers
                </span>
              </div>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0F172A] mb-4">
                Apply or get seller support
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Loado seller availability is currently open for Chennai. Share
                your store details and our team will help with onboarding,
                product listing, and order management on sell.loado.in.
              </p>
              <div className="p-5 bg-white border border-slate-200 rounded-lg">
                <p className="text-sm font-semibold text-[#0F172A] mb-2">Seller website</p>
                <a
                  href="https://sell.loado.in"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[#0055FF] font-bold hover:underline"
                >
                  sell.loado.in
                  <ArrowSquareOut size={18} weight="bold" />
                </a>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-8 lg:p-10 border border-slate-200 rounded-lg">
              {isSuccess ? (
                <div data-testid="supplier-support-success" className="text-center py-12">
                  <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Request Submitted</h3>
                  <p className="text-slate-600 mb-6">
                    Our seller support team will contact you shortly.
                  </p>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="text-[#0055FF] font-medium hover:underline"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="supplier_name" className="text-sm font-medium text-slate-700">
                        Name *
                      </Label>
                      <Input
                        id="supplier_name"
                        data-testid="supplier-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Supplier Name"
                        required
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="supplier_mobile" className="text-sm font-medium text-slate-700">
                        Mobile Number *
                      </Label>
                      <Input
                        id="supplier_mobile"
                        data-testid="supplier-mobile-input"
                        type="tel"
                        value={formData.mobile_number}
                        onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                        placeholder="9876543210"
                        required
                        minLength={10}
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="supplier_store_name" className="text-sm font-medium text-slate-700">
                        Store Name *
                      </Label>
                      <Input
                        id="supplier_store_name"
                        data-testid="supplier-store-name-input"
                        value={formData.store_name}
                        onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                        placeholder="ABC Traders"
                        required
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="supplier_city" className="text-sm font-medium text-slate-700">
                        Store Location City *
                      </Label>
                      <Input
                        id="supplier_city"
                        data-testid="supplier-city-input"
                        value={formData.store_location_city}
                        onChange={(e) => setFormData({ ...formData, store_location_city: e.target.value })}
                        placeholder="Chennai"
                        required
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="supplier_material" className="text-sm font-medium text-slate-700">
                      Material Category *
                    </Label>
                    <Select
                      value={formData.material_category}
                      onValueChange={(value) => setFormData({ ...formData, material_category: value })}
                    >
                      <SelectTrigger data-testid="supplier-material-select" className="mt-1.5">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {materialCategories.map((category) => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="supplier_message" className="text-sm font-medium text-slate-700">
                      Message *
                    </Label>
                    <Textarea
                      id="supplier_message"
                      data-testid="supplier-message-input"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your store, material supply, or support need."
                      rows={5}
                      required
                      className="mt-1.5 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="supplier-submit-btn"
                    disabled={
                      isLoading ||
                      !formData.name ||
                      !formData.mobile_number ||
                      !formData.store_name ||
                      !formData.store_location_city ||
                      !formData.material_category ||
                      !formData.message
                    }
                    className="w-full py-3 bg-[#0055FF] text-white font-semibold hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 rounded-lg"
                  >
                    {isLoading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={20} weight="bold" />
                        Submit Support Request
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
